import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { classApi } from '../../../api/classes';
import { formatCurrencyFromMinor } from '../../../utils/currency';
import { isOrgPaymentsNotReady, orgPaymentsNotReadyMessage } from '../../../utils/orgPayments';
import SupplierPolicyAcknowledgement, { policyToSections } from '../../../components/SupplierPolicyAcknowledgement';
import './ClassPaymentPage.css';
import { getStripePromise } from "../../../utils/stripeKey";
import ServiceFeeInfo from "../../../components/ServiceFeeInfo";


/**
 * PaymentForm — must be a child of <Elements> so Stripe hooks work
 */
const PaymentForm = ({ paymentData, formatAmount, onPaymentSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaying, setIsPaying] = useState(false);
    const [isPaid, setIsPaid] = useState(false);
    const [policyAck, setPolicyAck] = useState(false);
    const [elementsReady, setElementsReady] = useState(false);

    const handlePay = async () => {
        if (!stripe || !elements || !elementsReady || !paymentData) return;
        if (!policyAck) return;
        setIsPaying(true);
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: 'if_required',
            });

            if (error) {
                toast.error(error.message || 'Payment failed. Please try again.');
                return;
            }

            if (paymentIntent?.status === 'succeeded') {
                const confirmResponse = await classApi.confirmPayment(paymentData.paymentId, paymentIntent.id);
                if (!confirmResponse.ok) {
                    toast.error(confirmResponse.data?.message || 'Payment recorded but confirmation failed. Contact the studio.');
                    return;
                }
                setIsPaid(true);
                if (onPaymentSuccess) onPaymentSuccess();
            } else {
                toast.error('Payment not completed. Please try again.');
            }
        } catch {
            toast.error('Payment failed. Please try again.');
        } finally {
            setIsPaying(false);
        }
    };

    if (isPaid) {
        return (
            <div className="class-payment-success-card">
                <h2>Payment Complete!</h2>
                {paymentData.isAdjustment ? (
                    <>
                        <p>Your payment has been received. Thank you!</p>
                        <p style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--dl-color-medium)' }}>
                            <strong>Close</strong> this window to return to where you came from.
                        </p>
                    </>
                ) : (
                    <>
                        <p>Your enrolment has been confirmed. See you in class!</p>
                        <p style={{ marginTop: '12px', fontSize: '0.85rem', color: 'var(--dl-color-medium)' }}>
                            <strong>Close</strong> this window to return to where you came from, or view your enrolments in{' '}
                            <a href="/my-hub" style={{ color: 'var(--dl-color-purple-default)' }}>My Hub</a>.
                        </p>
                    </>
                )}
            </div>
        );
    }

    return (
        <>
            {paymentData.baseAmount && (
                <div className="class-payment-fee-breakdown">
                    <div className="class-payment-fee-row">
                        <span>{paymentData.isAdjustment ? (paymentData.adjustmentReason || 'Amount') : 'Class fee'}</span>
                        <span>{formatAmount(paymentData.baseAmount, paymentData.currency)}</span>
                    </div>
                    {(paymentData.platformFee > 0 || paymentData.processingFee > 0) && (
                        <div className="class-payment-fee-row">
                            <ServiceFeeInfo />
                            <span>{formatAmount((paymentData.platformFee || 0) + (paymentData.processingFee || 0), paymentData.currency)}</span>
                        </div>
                    )}
                </div>
            )}
            <div className="class-payment-total">
                <span>Total Due</span>
                <span className="class-payment-amount">
                    {formatAmount(paymentData.amount, paymentData.currency)}
                </span>
            </div>

            {paymentData.classEnrolmentPolicy && (
                <SupplierPolicyAcknowledgement
                    sections={policyToSections(paymentData.classEnrolmentPolicy)}
                    supplierName={paymentData.orgName}
                    surfaceLabel="class enrolment"
                    acknowledged={policyAck}
                    onAcknowledgedChange={setPolicyAck}
                />
            )}

            <div className="class-payment-card-element">
                <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => toast.error(e?.error?.message || 'Could not load the payment form. Please try again.')} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card'] }} />
            </div>

            <button
                className="class-payment-btn"
                onClick={handlePay}
                disabled={isPaying || !stripe || !elements || !elementsReady || !policyAck}>
                {isPaying ? 'Processing...' : `Pay ${formatAmount(paymentData.amount, paymentData.currency)}`}
            </button>

            <p className="class-payment-secure-note">
                Secured by Stripe. Your card details are never stored.
            </p>
        </>
    );
};

/**
 * ClassPaymentPage - Public QR payment page for class enrolments
 * Route: /class-payment/:qrToken
 * No auth required — accessed via QR code or email link
 */
const ClassPaymentPage = () => {
    const { qrToken } = useParams();

    const [paymentData, setPaymentData] = useState(null);
    const [clientSecret, setClientSecret] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [orgNotReady, setOrgNotReady] = useState(false);
    const [isPaid, setIsPaid] = useState(false);

    useEffect(() => {
        const branding = paymentData?.branding;
        if (!branding) return;
        const root = document.getElementById('class-payment-root');
        if (!root) return;
        if (branding.buttonColor) root.style.setProperty('--school-btn-color', branding.buttonColor);
        if (branding.buttonTextColor) root.style.setProperty('--school-btn-text', branding.buttonTextColor);
        if (branding.headerColor) root.style.setProperty('--school-header-color', branding.headerColor);
        if (branding.headerTextColor) root.style.setProperty('--school-header-text', branding.headerTextColor);
        if (branding.backgroundColor) root.style.setProperty('--school-bg-color', branding.backgroundColor);
    }, [paymentData]);

    useEffect(() => {
        const fetchPaymentData = async () => {
            if (qrToken === 'demo') {
                setPaymentData({
                    orgName: 'Latin Fusion Dance Studio',
                    branding: {
                        headerColor: '#ffb1e6',
                        headerTextColor: '#4a0a3a',
                        buttonColor: '#ffb1e6',
                        buttonTextColor: '#4a0a3a',
                        backgroundColor: '#fff5fc',
                    },
                    enrolments: [
                        { id: '1', className: 'Salsa Intermediate', style: 'Salsa', level: 'Intermediate' },
                        { id: '2', className: 'Bachata Beginners', style: 'Bachata', level: 'Beginner' },
                    ],
                    pricingOptionName: 'Term Package — 8 Weeks',
                    amount: 24000,
                    currency: 'AUD',
                    paymentId: 'demo',
                    firstName: 'Sarah',
                    lastName: 'Mitchell',
                });
                setIsLoading(false);
                return;
            }
            try {
                const response = await classApi.resolveQrToken(qrToken);
                if (response.ok) {
                    setPaymentData(response.data);
                    // Create payment intent upfront so clientSecret is available for Elements mount
                    const intentResponse = await classApi.createPaymentIntent(response.data.paymentId);
                    if (intentResponse.ok) {
                        setClientSecret(intentResponse.data.clientSecret);
                    } else if (isOrgPaymentsNotReady(intentResponse)) {
                        setOrgNotReady(true);
                    } else {
                        setError(intentResponse.data?.message || 'Failed to initialise payment');
                    }
                } else {
                    const msg = response.data?.message || 'Payment link not found';
                    setError(msg);
                }
            } catch {
                setError('Failed to load payment details');
            } finally {
                setIsLoading(false);
            }
        };
        fetchPaymentData();
    }, [qrToken]);

    if (isLoading) {
        return (
            <div className="class-payment-page" id="class-payment-root">
                <div className="class-payment-loading">Loading payment details...</div>
            </div>
        );
    }

    if (orgNotReady) {
        return (
            <div className="class-payment-page" id="class-payment-root">
                <div className="class-payment-error-card">
                    <h2>Online Payments Not Available</h2>
                    <p>{orgPaymentsNotReadyMessage(paymentData?.orgName, 'the school')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="class-payment-page" id="class-payment-root">
                <div className="class-payment-error-card">
                    <h2>Payment Link Invalid</h2>
                    <p>{error}</p>
                    <p>This link may have expired or already been used. Please ask your instructor for a new link.</p>
                </div>
            </div>
        );
    }

    const formatAmount = (cents, currency) => formatCurrencyFromMinor(cents, currency);

    return (
        <div className="class-payment-page" id="class-payment-root">
            <div className="class-payment-header">
                <h1 className="class-payment-org-name">{paymentData.orgName}</h1>
                <p className="class-payment-subtitle">Class Enrolment Payment</p>
                {(paymentData.firstName || paymentData.lastName) && (
                    <p className="class-payment-student-name">
                        For {paymentData.firstName} {paymentData.lastName}
                    </p>
                )}
            </div>

            <div className="class-payment-card">
                <h2 className="class-payment-section-title">Classes Enrolled</h2>
                <ul className="class-payment-enrolment-list">
                    {(paymentData.enrolments || []).map(enrol => (
                        <li key={enrol.id} className="class-payment-enrolment-item">
                            <span className="class-payment-class-name">{enrol.className}</span>
                            <span className="class-payment-class-level">{enrol.style} {enrol.level}</span>
                        </li>
                    ))}
                </ul>

                {paymentData.pricingOptionName && (
                    <div className="class-payment-pricing-info">
                        <span>Package:</span>
                        <span>{paymentData.pricingOptionName}</span>
                    </div>
                )}

                {qrToken === 'demo' ? (
                    <>
                        <div className="class-payment-total">
                            <span>Total Due</span>
                            <span className="class-payment-amount">{formatAmount(paymentData.amount, paymentData.currency)}</span>
                        </div>
                        <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '24px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', marginBottom: '16px' }}>
                            <p style={{ margin: 0, fontWeight: 600 }}>Stripe Payment Form</p>
                            <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>Card / Apple Pay / Google Pay would appear here</p>
                        </div>
                        <button className="class-payment-btn" disabled>Pay {formatAmount(paymentData.amount, paymentData.currency)}</button>
                    </>
                ) : clientSecret ? (
                    <Elements stripe={getStripePromise()} options={{
                        clientSecret,
                        appearance: {
                            theme: 'stripe',
                            variables: {
                                colorPrimary: paymentData?.branding?.buttonColor || paymentData?.branding?.headerColor || '#451f78',
                                colorBackground: '#ffffff',
                                colorText: '#1a1a1a',
                                borderRadius: '8px',
                                fontFamily: 'system-ui, sans-serif',
                            },
                        },
                    }}>
                        <PaymentForm paymentData={paymentData} formatAmount={formatAmount} onPaymentSuccess={() => setIsPaid(true)} />
                    </Elements>
                ) : (
                    <div className="class-payment-loading" style={{ padding: '20px', textAlign: 'center' }}>
                        Preparing payment...
                    </div>
                )}
                {!isPaid && (
                    <div style={{ textAlign: 'right', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--dl-color-border)' }}>
                        <button
                            onClick={() => window.history.back()}
                            style={{ background: 'none', border: 'none', color: 'var(--dl-color-gray-400)', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0' }}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClassPaymentPage;
